import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, PenLine } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ value, onChange, size = 18 }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={onChange ? `review-star-${n}` : undefined}
          onClick={onChange ? () => onChange(n) : undefined}
          className={onChange ? "hover:scale-110 transition-transform duration-200" : "cursor-default"}
          aria-label={`${n} stelle`}
        >
          <Star
            size={size}
            strokeWidth={1.5}
            className={n <= value ? "fill-terracotta text-terracotta" : "text-sand"}
          />
        </button>
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nome: "", eta: "", stelle: 5, motivo: "", testo: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((res) => setReviews(res.data)).catch(() => setReviews([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/reviews`, { ...form, eta: parseInt(form.eta, 10) });
      toast.success("Grazie! La recensione sarà pubblicata dopo l'approvazione.");
      setForm({ nome: "", eta: "", stelle: 5, motivo: "", testo: "" });
      setShowForm(false);
    } catch {
      toast.error("Invio non riuscito. Controlla i campi e riprova.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="recensioni" data-testid="reviews-section" className="py-24 lg:py-32 bg-cream border-y border-pine/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-terracotta mb-4">Recensioni</p>
              <h2 className="font-serif tracking-tight text-pine text-3xl sm:text-4xl lg:text-5xl max-w-xl">
                Le parole di chi si è rimesso in movimento.
              </h2>
            </div>
            <button
              data-testid="leave-review-button"
              onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-2 rounded-full bg-pine text-bone font-semibold px-6 py-3.5 hover:bg-terracotta transition-colors duration-300"
            >
              <PenLine size={16} strokeWidth={1.5} /> Lascia una recensione
            </button>
          </div>
        </Reveal>

        {showForm && (
          <Reveal>
            <form
              data-testid="review-form"
              onSubmit={submit}
              className="bg-paper border border-pine/10 rounded-md p-8 lg:p-10 mb-12"
            >
              <div className="grid sm:grid-cols-3 gap-6 items-end">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-moss">Nome</span>
                  <input
                    data-testid="review-name-input"
                    required
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    placeholder="Il tuo nome"
                    className="mt-2 w-full bg-transparent border-b border-pine/25 focus:border-terracotta outline-none py-2.5 text-pine placeholder:text-moss/40 transition-colors duration-300"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-moss">Età</span>
                  <input
                    data-testid="review-age-input"
                    required
                    type="number"
                    min="1"
                    max="110"
                    value={form.eta}
                    onChange={(e) => setForm({ ...form, eta: e.target.value })}
                    placeholder="Es. 45"
                    className="mt-2 w-full bg-transparent border-b border-pine/25 focus:border-terracotta outline-none py-2.5 text-pine placeholder:text-moss/40 transition-colors duration-300"
                  />
                </label>
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-moss">Valutazione</span>
                  <div className="mt-3">
                    <Stars value={form.stelle} onChange={(stelle) => setForm({ ...form, stelle })} size={24} />
                  </div>
                </div>
              </div>
              <label className="block mt-6">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-moss">
                  Motivo del percorso <span className="normal-case font-normal">(facoltativo)</span>
                </span>
                <input
                  data-testid="review-reason-input"
                  value={form.motivo}
                  onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                  placeholder="Es. mal di schiena, riabilitazione al ginocchio..."
                  className="mt-2 w-full bg-transparent border-b border-pine/25 focus:border-terracotta outline-none py-2.5 text-pine placeholder:text-moss/40 transition-colors duration-300"
                />
              </label>
              <label className="block mt-6">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-moss">La tua esperienza</span>
                <textarea
                  data-testid="review-text-input"
                  required
                  rows={3}
                  value={form.testo}
                  onChange={(e) => setForm({ ...form, testo: e.target.value })}
                  placeholder="Racconta come è andata, in poche parole..."
                  className="mt-2 w-full bg-transparent border-b border-pine/25 focus:border-terracotta outline-none py-2.5 text-pine placeholder:text-moss/40 resize-none transition-colors duration-300"
                />
              </label>
              <button
                data-testid="review-submit-button"
                type="submit"
                disabled={sending}
                className="mt-6 rounded-full bg-terracotta text-bone font-semibold px-8 py-3.5 hover:bg-pine disabled:opacity-60 transition-colors duration-300"
              >
                {sending ? "Invio in corso..." : "Invia recensione"}
              </button>
              <p className="mt-3 text-xs text-moss/70">
                Le recensioni vengono verificate prima di essere pubblicate.
              </p>
            </form>
          </Reveal>
        )}

        {!reviews ? (
          <p className="text-moss" data-testid="reviews-loading">Caricamento recensioni...</p>
        ) : reviews.length === 0 ? (
          <Reveal>
            <div data-testid="reviews-empty" className="border border-dashed border-pine/20 rounded-md p-12 text-center">
              <Stars value={5} size={22} />
              <p className="mt-4 font-serif text-xl text-pine">Ancora nessuna recensione pubblicata</p>
              <p className="mt-2 text-sm text-moss">Se sei stato mio paziente, la tua esperienza può aiutare gli altri a scegliere.</p>
            </div>
          </Reveal>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.08}>
                <article data-testid={`review-card-${r.id}`} className="bg-paper border border-pine/10 rounded-md p-8 h-full flex flex-col">
                  <Stars value={r.stelle} />
                  {r.motivo && (
                    <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-terracotta">{r.motivo}</p>
                  )}
                  <p className="mt-5 text-sm lg:text-base text-moss leading-relaxed flex-1">"{r.testo}"</p>
                  <p className="mt-6 text-sm font-semibold text-pine">
                    {r.nome} <span className="font-normal text-moss">· {r.eta} anni</span>
                  </p>
                  {r.fonte && (
                    <p className="mt-1 text-xs text-moss/70">Recensione da {r.fonte}</p>
                  )}
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
